import React, { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { DriverAdminTabs } from '../../components/admin/DriverAdminTabs';
import { Link2, Car, User, Loader2, CheckCircle2, AlertCircle, Clock } from 'lucide-react';
interface Driver {
  id: number;
  full_name: string;
  phone: string;
}
interface Vehicle {
  id: number;
  plate: string;
  vehicle_type: string;
  brand: string | null;
  model: string | null;
}
interface Assignment {
  id: number;
  assigned_at: string;
  drivers: {
    full_name: string;
    phone: string;
  };
  vehicles: {
    plate: string;
    vehicle_type: string;
  };
}
export function AssignmentsPage() {
  const [drivers, setDrivers] = useState<Driver[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [selectedDriverId, setSelectedDriverId] = useState<string>('');
  const [selectedVehicleId, setSelectedVehicleId] = useState<string>('');
  const [currentVehicle, setCurrentVehicle] = useState<Vehicle | null>(null);
  const [loadingData, setLoadingData] = useState(true);
  const [isAssigning, setIsAssigning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  useEffect(() => {
    fetchInitialData();
  }, []);
  useEffect(() => {
    if (selectedDriverId) {
      fetchCurrentVehicleForDriver(parseInt(selectedDriverId));
    } else {
      setCurrentVehicle(null);
    }
  }, [selectedDriverId]);
  const fetchInitialData = async () => {
    setLoadingData(true);
    const [driversRes, vehiclesRes, assignmentsRes] = await Promise.all([supabase.from('drivers').select('id, full_name, phone').eq('is_active', true).order('full_name'), supabase.from('vehicles').select('id, plate, vehicle_type, brand, model').eq('is_active', true).order('plate'), supabase.from('driver_vehicle_assignments').select('id, assigned_at, drivers(full_name, phone), vehicles(plate, vehicle_type)').eq('is_active', true).order('assigned_at', {
      ascending: false
    })]);
    if (driversRes.data) setDrivers(driversRes.data);
    if (vehiclesRes.data) setVehicles(vehiclesRes.data);
    if (assignmentsRes.data) setAssignments(assignmentsRes.data as any);
    setLoadingData(false);
  };
  const fetchCurrentVehicleForDriver = async (driverId: number) => {
    try {
      const {
        data,
        error
      } = await supabase.rpc('get_active_vehicle_for_driver', {
        p_driver_id: driverId
      });
      if (!error && data) {
        setCurrentVehicle(Array.isArray(data) ? data[0] : data);
      } else {
        setCurrentVehicle(null);
      }
    } catch (err) {
      setCurrentVehicle(null);
    }
  };
  const handleAssign = async () => {
    if (!selectedDriverId || !selectedVehicleId) return;
    setIsAssigning(true);
    setError(null);
    setSuccess(null);
    try {
      const {
        error: rpcError
      } = await supabase.rpc('assign_vehicle_to_driver', {
        p_driver_id: parseInt(selectedDriverId),
        p_vehicle_id: parseInt(selectedVehicleId)
      });
      if (rpcError) throw rpcError;
      setSuccess('Vehículo asignado correctamente.');
      await fetchInitialData();
      await fetchCurrentVehicleForDriver(parseInt(selectedDriverId));
      setSelectedVehicleId('');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err: any) {
      console.error('[AssignVehicle] Error:', err);
      setError(err.message || 'Error al asignar el vehículo.');
    } finally {
      setIsAssigning(false);
    }
  };
  return <div className="min-h-screen bg-[#0B0B0B] text-white font-sans" data-id="element-1465">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8 space-y-8" data-id="element-1466">
        <DriverAdminTabs data-id="element-1467" />

        {/* Header */}
        <div className="flex items-center gap-3" data-id="element-1468">
          <div className="w-12 h-12 bg-[#141414] border border-white/10 rounded-2xl flex items-center justify-center" data-id="element-1469">
            <Link2 className="w-6 h-6 text-[#006847]" data-id="element-1470" />
          </div>
          <div data-id="element-1471">
            <h1 className="text-2xl font-bold" data-id="element-1472">Asignar Vehículos</h1>
            <p className="text-white/40 text-sm" data-id="element-1473">
              Vincula conductores con vehículos activos
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8" data-id="element-1474">
          {/* Assignment Form */}
          <div className="lg:col-span-5 space-y-6" data-id="element-1475">
            <div className="bg-[#141414] border border-white/10 rounded-2xl p-6 shadow-2xl" data-id="element-1476">
              <h2 className="text-lg font-bold mb-6" data-id="element-1477">Nueva Asignación</h2>

              <div className="space-y-5" data-id="element-1478">
                {/* Driver Select */}
                <div data-id="element-1479">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-1480">
                    Conductor
                  </label>
                  <div className="relative" data-id="element-1481">
                    <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-1482" />
                    <select value={selectedDriverId} onChange={e => setSelectedDriverId(e.target.value)} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] appearance-none" data-id="element-1483">
                      <option value="" data-id="element-1484">Seleccionar conductor...</option>
                      {drivers.map(d => <option key={d.id} value={d.id} data-id="element-1485">
                          {d.full_name} ({d.phone})
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Current Vehicle Info */}
                {selectedDriverId && currentVehicle && <div className="bg-[#006847]/10 border border-[#006847]/20 rounded-xl p-4 flex items-center gap-3" data-id="element-1486">
                    <Car className="w-5 h-5 text-[#00a870]" data-id="element-1487" />
                    <div data-id="element-1488">
                      <p className="text-xs text-[#00a870] font-bold uppercase tracking-wider" data-id="element-1489">
                        Vehículo actual
                      </p>
                      <p className="text-sm font-mono text-white mt-0.5" data-id="element-1490">
                        {currentVehicle.plate}{' '}
                        <span className="font-sans text-white/50" data-id="element-1491">
                          ({currentVehicle.vehicle_type})
                        </span>
                      </p>
                    </div>
                  </div>}
                {selectedDriverId && !currentVehicle && <div className="bg-white/5 border border-white/10 rounded-xl p-4 flex items-center gap-3" data-id="element-1492">
                    <Car className="w-5 h-5 text-white/30" data-id="element-1493" />
                    <p className="text-sm text-white/50" data-id="element-1494">
                      Sin vehículo asignado actualmente.
                    </p>
                  </div>}

                {/* Vehicle Select */}
                <div data-id="element-1495">
                  <label className="block text-xs font-bold text-white/40 uppercase tracking-widest mb-2" data-id="element-1496">
                    Vehículo a asignar
                  </label>
                  <div className="relative" data-id="element-1497">
                    <Car className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-white/20" data-id="element-1498" />
                    <select value={selectedVehicleId} onChange={e => setSelectedVehicleId(e.target.value)} className="w-full bg-[#0B0B0B] border border-white/10 rounded-xl pl-12 pr-4 py-4 text-base text-white focus:outline-none focus:border-[#006847] focus:ring-1 focus:ring-[#006847] appearance-none" data-id="element-1499">
                      <option value="" data-id="element-1500">Seleccionar vehículo...</option>
                      {vehicles.map(v => <option key={v.id} value={v.id} data-id="element-1501">
                          {v.plate} - {v.vehicle_type}{' '}
                          {v.brand ? `(${v.brand})` : ''}
                        </option>)}
                    </select>
                  </div>
                </div>

                {/* Messages */}
                {error && <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-start gap-3 text-red-400" data-id="element-1502">
                    <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1503" />
                    <div className="text-sm whitespace-pre-line" data-id="element-1504">{error}</div>
                  </div>}
                {success && <div className="p-4 bg-[#006847]/10 border border-[#006847]/20 rounded-xl flex items-start gap-3 text-[#00a870]" data-id="element-1505">
                    <CheckCircle2 className="w-5 h-5 shrink-0 mt-0.5" data-id="element-1506" />
                    <div className="text-sm" data-id="element-1507">{success}</div>
                  </div>}

                {/* Submit */}
                <button onClick={handleAssign} disabled={isAssigning || !selectedDriverId || !selectedVehicleId} className="w-full py-5 bg-[#006847] hover:bg-[#005a3d] disabled:bg-white/5 disabled:text-white/30 text-white font-black text-lg rounded-xl transition-all flex items-center justify-center gap-2 active:scale-95 mt-4" data-id="element-1508">
                  {isAssigning ? <Loader2 className="w-6 h-6 animate-spin" data-id="element-1509" /> : <>
                      <Link2 className="w-6 h-6" data-id="element-1510" />
                      Asignar Vehículo
                    </>}
                </button>
              </div>
            </div>
          </div>

          {/* Active Assignments Table */}
          <div className="lg:col-span-7" data-id="element-1511">
            <div className="bg-[#141414] border border-white/10 rounded-2xl overflow-hidden shadow-2xl" data-id="element-1512">
              <div className="p-6 border-b border-white/5" data-id="element-1513">
                <h2 className="text-lg font-bold" data-id="element-1514">Asignaciones Activas</h2>
              </div>

              {loadingData ? <div className="flex justify-center py-12" data-id="element-1515">
                  <Loader2 className="w-8 h-8 text-[#006847] animate-spin" data-id="element-1516" />
                </div> : assignments.length === 0 ? <div className="text-center py-12" data-id="element-1517">
                  <Link2 className="w-12 h-12 text-white/10 mx-auto mb-3" data-id="element-1518" />
                  <p className="text-white/40" data-id="element-1519">No hay asignaciones activas</p>
                </div> : <div className="overflow-x-auto" data-id="element-1520">
                  <table className="w-full text-left text-sm" data-id="element-1521">
                    <thead className="bg-[#0B0B0B] text-white/40 uppercase tracking-wider text-[10px]" data-id="element-1522">
                      <tr data-id="element-1523">
                        <th className="px-6 py-4 font-bold" data-id="element-1524">Conductor</th>
                        <th className="px-6 py-4 font-bold" data-id="element-1525">Vehículo</th>
                        <th className="px-6 py-4 font-bold text-right" data-id="element-1526">
                          Asignado
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-white/5" data-id="element-1527">
                      {assignments.map(a => <tr key={a.id} className="hover:bg-white/5 transition-colors" data-id="element-1528">
                          <td className="px-6 py-4" data-id="element-1529">
                            <div className="font-bold text-white" data-id="element-1530">
                              {a.drivers?.full_name}
                            </div>
                            <div className="text-white/40 text-xs mt-0.5" data-id="element-1531">
                              {a.drivers?.phone}
                            </div>
                          </td>
                          <td className="px-6 py-4" data-id="element-1532">
                            <div className="font-mono font-bold text-white" data-id="element-1533">
                              {a.vehicles?.plate}
                            </div>
                            <div className="text-white/40 text-xs mt-0.5 uppercase" data-id="element-1534">
                              {a.vehicles?.vehicle_type}
                            </div>
                          </td>
                          <td className="px-6 py-4 text-right text-white/40" data-id="element-1535">
                            <div className="flex items-center justify-end gap-1.5" data-id="element-1536">
                              <Clock className="w-3.5 h-3.5" data-id="element-1537" />
                              {new Date(a.assigned_at).toLocaleDateString()}
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                  </table>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </div>;
}